import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';

const expertiseItemIds = ['supply-chain', 'europe-career', 'young-professionals'];

const expertiseImages: Record<string, string> = {
  'supply-chain': 'https://readdy.ai/api/search-image?query=Modern%20supply%20chain%20and%20logistics%20operations%20center%20with%20large%20digital%20dashboards%20showing%20global%20shipping%20routes%20and%20real%20time%20inventory%20data%20clean%20professional%20environment%20with%20navy%20blue%20and%20white%20color%20tones%20sophisticated%20industrial%20aesthetic%20professional%20editorial%20photography%20with%20soft%20lighting&width=900&height=600&seq=expertise-detail-supplychain-01&orientation=landscape',
  'europe-career': 'https://readdy.ai/api/search-image?query=Aerial%20panorama%20of%20a%20modern%20European%20city%20skyline%20with%20contemporary%20glass%20office%20towers%20and%20green%20parks%20on%20a%20bright%20sunny%20day%20with%20soft%20white%20clouds%20professional%20aspirational%20atmosphere%20warm%20natural%20lighting%20wide%20angle%20editorial%20photography&width=900&height=600&seq=expertise-detail-europe-02&orientation=landscape',
  'young-professionals': 'https://readdy.ai/api/search-image?query=Young%20professional%20working%20at%20a%20bright%20modern%20co%20working%20space%20with%20laptop%20and%20notebook%20surrounded%20by%20green%20plants%20natural%20light%20streaming%20through%20floor%20to%20ceiling%20windows%20clean%20contemporary%20minimalist%20interior%20design%20fresh%20energetic%20productive%20vibe%20professional%20lifestyle%20photography&width=900&height=600&seq=expertise-detail-youngpro-03&orientation=landscape',
};

export default function Expertise() {
  const { t } = useTranslation();

  return (
    <Layout>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-primary-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-accent-500 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-accent-400 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-300 mb-4">
            {t('expertise.label')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t('expertisePage.heroTitle')}
          </h1>
          <p className="text-base text-foreground-200 max-w-[600px] mx-auto leading-relaxed">
            {t('expertisePage.heroSubtitle')}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {expertiseItemIds.map((id) => (
              <div
                key={id}
                className="group relative h-[480px] rounded-2xl overflow-hidden cursor-pointer"
              >
                <img
                  src={expertiseImages[id]}
                  alt={t(`expertise.items.${id}.title`)}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/30 to-transparent" />

                <div className="absolute inset-0 flex flex-col justify-end p-7 md:p-9">
                  <p className="text-xs font-semibold uppercase tracking-widest text-accent-300 mb-2">
                    {t(`expertise.items.${id}.subtitle`)}
                  </p>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
                    {t(`expertise.items.${id}.title`)}
                  </h3>
                  <p className="text-sm text-foreground-200 leading-relaxed max-w-sm">
                    {t(`expertise.items.${id}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-background-100">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-12 text-center">
            {t('expertisePage.whyTitle')}
          </h2>
          <div className="space-y-8">
            {[1, 2, 3].map((idx) => (
              <div key={idx} className="flex gap-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                  <i className="ri-check-line text-accent-600 text-lg" />
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed pt-2">
                  {t(`expertisePage.why${idx}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-16 bg-primary-500">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            {t('expertisePage.cta')}
          </h2>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-primary-700 font-semibold text-sm rounded-full hover:bg-accent-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta1')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}